import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePermission } from "hooks/usePermission";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Icons } from "assets/icons";
import { CsvIcon, ExcelIcon, formatUTCDate, getMinMaxDate } from "utils/utils";
import { getActionFlags } from "utils/getActionFlags";
import FilterBar from "components/common/FilterBar";
import Switch from "components/common/Switch";
import { CustomerStatusBadge } from "components/common/StatusBadge";
import {
  getCustomers,
  resetFilters,
  setFilter,
  updateCustomerStatus,
} from "store/slices/leadsSlice";
import {
  CUSTOMER_STATUS_OPTIONS,
  ORDER_BY_OPTIONS,
  PLATFORM_OPTIONS,
} from "utils/constants";
import NoDataFound from "components/common/NoDataFound";
import TableShimmer from "components/common/TableShimmer";
import Paginations from "components/common/Paginations";
import { handleStatusUpdate } from "helpers/apiUtils";
import DataTable from "components/common/table/DataTable";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import { Download } from "lucide-react";
import DownloadExcelFile from "helpers/excelDownloadHandler";
import {
  downloadCustomerReports,
  downloadCustomerReportsExcel,
} from "store/slices/reportsDownloadSlice";
import { Menu } from "primereact/menu";
import { Button } from "primereact/button";
import { showAlert } from "store/slices/alertSlice";
import TruncatedText from "components/common/TruncatedText";
import { getDashboardData } from "store/slices/dashboardSlice";
import LeadsGrowthChart from "pages/Dashboard/Customers/LeadsGrowthChart";
import { usePageParam } from "hooks/usePageParam";

const LOCATION_OPTIONS = [
  { name: "All", value: "" },
  { name: "Location 1 (Al-Arad)", value: "1" },
  { name: "Location 2 (Al-Taawun)", value: "2" },
];

const LOCATION_NAMES = {
  "1": "Location 1 (Al-Arad)",
  "2": "Location 2 (Al-Taawun)",
  "Al-Arad": "Location 1 (Al-Arad)",
  "Al-Taawun": "Location 2 (Al-Taawun)",
  "Tawun": "Location 2 (Al-Taawun)",
};

const getLocationName = (location) => {
  return LOCATION_NAMES[location] || location || "Location 1 (Al-Arad)";
};

const Leads = () => {
  const { t } = useTranslation();
  const { noCustomers } = Icons || {};
  const dispatch = useDispatch();
  const [pageParam, setPageParam] = usePageParam();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit } = canAction("customers");
  const exportMenuRef = useRef(null);
  const [leadStatsDates, setLeadStatsDates] = useState([]);
  const [activeLeadStatFilter, setActiveLeadStatFilter] = useState("");

  const {
    items: customers,
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy, platform, locationName },
  } = useSelector((state) => state.Leads);
  const chartsData = useSelector((state) => state.Dashboard?.data?.leadsChart);
  const membershipGrowthData = chartsData?.leadsGrowth;

  useEffect(() => {
    if (canView) {
      getAllData(pageParam);
    }
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    setPageParam(page || 1);

    const payload = {
      type: "lead",
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      status: isReset ? -1 : status,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
      platform: isReset || platform.value === "" ? "" : platform,
      locationName: isReset ? "" : locationName?.value ?? locationName ?? "",
    };
    dispatch(getCustomers(payload));
  };

  const handleReset = useCallback(() => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  }, []);

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: CUSTOMER_STATUS_OPTIONS,
      },
      {
        key: "orderBy",
        placeholder: t("Order By"),
        type: "singleSelect",
        options: ORDER_BY_OPTIONS,
      },
      {
        key: "platform",
        placeholder: t("Platform"),
        type: "singleSelect",
        options: PLATFORM_OPTIONS,
      },
      {
        key: "locationName",
        placeholder: t("Location"),
        type: "singleSelect",
        options: LOCATION_OPTIONS,
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        key: "name",
        render: (value, item) =>
          value ? (
            <Link to={`/leads/lead-details?id=${item._id}`}>{value}</Link>
          ) : (
            "-"
          ),
      },
      {
        header: t("Phone Number"),
        key: "phone",
        render: (value) => <p className="ltr_text">{value || "-"}</p>,
      },
      {
        header: t("Location"),
        key: "locationName",
        render: (value) => (
          <p className="ltr_text" style={{ whiteSpace: "nowrap" }}>
            {getLocationName(value)}
          </p>
        ),
      },
      {
        header: t("Last Login Date"),
        key: "lastLoggedIn",
        render: (value) => <p className="ltr_text">{formatUTCDate(value)}</p>,
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Total Vehicles"),
        key: "totalVehicles",
        render: (value) => <div className="text-center">{value}</div>,
      },
      {
        header: t("Licence Plates"),
        key: "licensePlateNumbers",
        render: (value, item) => <TruncatedText text={value?.join(", ")} />,
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <CustomerStatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <div onClick={(event) => event.stopPropagation()}>
        {canEdit && (
          <Switch
            checked={item.status === 1}
            handleOnChange={() => {
              handleStatusUpdate({
                _id: item._id,
                status: item.status,
                dispatch,
                action: updateCustomerStatus,
              });
            }}
            disabled={isLoading && loadingItemId === item._id}
          />
        )}
      </div>
    ),
    [canEdit, dispatch, isLoading, loadingItemId],
  );
  const handleDownloadCustomers = (action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      orderBy: isReset ? -1 : orderBy,
      exportType: "csv",
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
      platform: isReset || platform.value === "" ? "" : platform,
      customerType: "lead",
    };
    dispatch(downloadCustomerReports(payload)).then((result) => {
      const data = result?.payload ?? result;

      const hasData = typeof data === "string" && data.trim().length > 0;

      if (!hasData) {
        dispatch(
          showAlert({
            text: t(`No CSV data found`),
            type: "error",
          }),
        );
        return;
      }
      DownloadExcelFile(data, "leads");
    });
  };
  const handleDownloadCustomersExcel = (action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      orderBy: isReset ? -1 : orderBy,
      exportType: "excel",
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
      platform: isReset || platform.value === "" ? "" : platform,
      customerType: "lead",
    };
    dispatch(downloadCustomerReportsExcel(payload)).then((result) => {
      const data = result?.payload ?? result;
      const hasData = data instanceof ArrayBuffer && data.byteLength > 0;

      if (!hasData) {
        dispatch(
          showAlert({
            text: t(`No Excel data found`),
            type: "error",
          }),
        );
        return;
      }
      DownloadExcelFile(data, "leads");
    });
  };
  const exportMenuItems = [
    {
      label: t("Export as CSV"),
      icon: <CsvIcon />,
      command: handleDownloadCustomers,
    },
    {
      label: t("Export as Excel"),
      icon: <ExcelIcon />,
      command: handleDownloadCustomersExcel,
    },
  ];
  const extraActions = (
    <div className="buttons export_buttons">
      <Menu
        model={exportMenuItems}
        popup
        ref={exportMenuRef}
        id="export-menu"
      />
      <Button
        label={t("Export")}
        icon={<Download size={16} />}
        onClick={(e) => exportMenuRef.current.toggle(e)}
        aria-controls="export-menu"
        aria-haspopup
        className="p-button-outlined"
      />
    </div>
  );
  const buildDashboardPayload = (subType = "leadsChart") => {
    if (leadStatsDates.length === 0) {
      return null;
    }
    const dateRange = getMinMaxDate(leadStatsDates);

    // Check for invalid epoch date
    const isInvalidDate =
      dateRange?.min?.includes("1970") || dateRange?.max?.includes("1970");

    if (isInvalidDate) {
      return null;
    }

    const payload = {
      dateRange,
      isWeekSelected: activeLeadStatFilter === "WEEK",
      timezone:
        Intl.DateTimeFormat().resolvedOptions().timeZone ?? "Asia/Riyadh",
    };

    if (subType) {
      payload.subType = subType;
    }

    return payload;
  };
  useEffect(() => {
    const dashboardPayload = buildDashboardPayload();
    if (!dashboardPayload) return;
    dispatch(
      getDashboardData({
        type: "customers",
        payload: dashboardPayload,
      }),
    );
  }, [leadStatsDates]);

  if (!canView) return <NoPermission />;

  return (
    <div className="page-content customers leads_page">
      <PageHeader title={t("Leads")} />

      <div className="dashboard_page bg-white p-2">
        <div className="customers_dashboard">
          <LeadsGrowthChart
            membershipGrowth={membershipGrowthData}
            activeFilterType={activeLeadStatFilter}
            showLeadsPageFilter={true}
            dates={leadStatsDates}
            setDates={setLeadStatsDates}
            setActiveFilter={setActiveLeadStatFilter}
            className={"mt-0"}
          />
        </div>
      </div>

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Name, Phone Number ,Location")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, platform, locationName }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        rightActions={extraActions}
        className="mt-5"
        searchMaxWidth={350}
      />
      {loading && <TableShimmer rows={7} columns={9} />}
      {!loading && customers?.length === 0 && (
        <NoDataFound title={t("No leads found")} icon={noCustomers} />
      )}
      {!loading && customers?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={customers}
            renderActions={renderActions}
            onRowClick={(item) =>
              navigate(`/leads/lead-details?id=${item._id}`)
            }
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default Leads;
